import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { getMeta, setMeta } from "../../../core/data/local/db";
import { parseCardInput, codeDigest, formatCode } from "../domain/code";
import { knownCard, type KnownCard } from "../data/records";
import { loyaltyTheme } from "../../../theme/loyalty";

/**
 * Scan a card (specs/006-loyalty research R8).
 *
 * THREE WAYS IN, ONE PARSER. The camera (Chrome's built-in BarcodeDetector),
 * a USB barcode scanner (which "types" the card URL and Enter into the focused
 * field), and typing the short code all end in `parseCardInput`, then a digest
 * lookup in the till's replica. Nothing here touches the network, so it works
 * the same offline.
 *
 * NEVER IN THE WAY. Closing the dialog leaves the sale exactly as it was
 * (FR-015). A camera that is missing, refused or slow only means typing.
 */

export interface AcceptedCard {
  code: string;
  digest: string;
  card: KnownCard;
}

interface ScanCardProps {
  onAccept(card: AcceptedCard): void;
  onClose(): void;
}

type Facing = "user" | "environment";
const CAMERA_KEY = "loyalty.camera";

interface Detector {
  detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
}
interface DetectorConstructor {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats(): Promise<string[]>;
}

function barcodeDetector(): DetectorConstructor | null {
  const candidate = (globalThis as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
  return candidate ?? null;
}

export function ScanCard({ onAccept, onClose }: ScanCardProps) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [camera, setCamera] = useState<"starting" | "on" | "unsupported" | "denied">("starting");
  const [facing, setFacing] = useState<Facing | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const settled = useRef(false);

  const tryInput = useCallback(
    async (raw: string): Promise<boolean> => {
      const parsed = parseCardInput(raw);
      if (!parsed.ok) {
        setError(parsed.reason === "check" ? loyaltyTheme.till.badCheck : parsed.reason === "length" ? loyaltyTheme.till.badLength : loyaltyTheme.till.notACard);
        return false;
      }
      const digest = await codeDigest(parsed.code);
      const card = await knownCard(digest);
      if (!card) {
        setError(loyaltyTheme.till.notACard);
        return false;
      }
      if (card.status !== "active") {
        setError(card.status === "blocked" ? loyaltyTheme.till.blocked : loyaltyTheme.till.retired);
        return false;
      }
      if (settled.current) return true;
      settled.current = true;
      onAccept({ code: parsed.code, digest, card });
      return true;
    },
    [onAccept],
  );

  // The camera the till last used: front by default, since the tablet faces
  // the customer, and remembered once a cashier switches it.
  useEffect(() => {
    void getMeta<Facing>(CAMERA_KEY).then((stored) => setFacing(stored ?? "user"));
  }, []);

  useEffect(() => {
    if (facing === null) return;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    void (async () => {
      const Detector = barcodeDetector();
      if (!Detector || !navigator.mediaDevices?.getUserMedia) {
        setCamera("unsupported");
        return;
      }
      try {
        const formats = await Detector.getSupportedFormats();
        if (!formats.includes("qr_code")) {
          setCamera("unsupported");
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      } catch {
        setCamera("denied");
        return;
      }
      if (stopped || !video.current) return;
      video.current.srcObject = stream;
      await video.current.play().catch(() => undefined);
      setCamera("on");

      const detector = new Detector({ formats: ["qr_code"] });
      const tick = async () => {
        if (stopped || !video.current) return;
        try {
          const found = await detector.detect(video.current);
          for (const code of found) {
            if (await tryInput(code.rawValue)) return;
          }
        } catch {
          // A frame that could not be read is not an error; try the next.
        }
        timer = setTimeout(() => void tick(), 200);
      };
      void tick();
    })();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [facing, tryInput]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void tryInput(typed);
  };

  const switchCamera = () => {
    const next: Facing = facing === "user" ? "environment" : "user";
    setCamera("starting");
    setFacing(next);
    void setMeta(CAMERA_KEY, next);
  };

  return (
    <div className="loy__overlay" role="dialog" aria-label={loyaltyTheme.till.scan}>
      <div className="loy__sheet">
        <header className="loy__head">
          <h2>{loyaltyTheme.till.scan}</h2>
          <button type="button" className="loy__close" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="loy__camera">
          <video ref={video} className={camera === "on" ? "is-on" : ""} muted playsInline />
          {camera === "unsupported" && <p className="loy__note">{loyaltyTheme.till.noCamera}</p>}
          {camera === "denied" && <p className="loy__note">{loyaltyTheme.till.cameraDenied}</p>}
          {(camera === "on" || camera === "starting") && (
            <button type="button" className="loy__switch" onClick={switchCamera}>
              Switch camera
            </button>
          )}
        </div>

        <form className="loy__type" onSubmit={submit}>
          <label>
            <span>{loyaltyTheme.till.typeHint}</span>
            <input
              autoFocus
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
                setError(null);
              }}
              placeholder={formatCode("K7M2QX9PAB3")}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={80}
            />
          </label>
          <button type="submit" className="loy__primary" disabled={typed.trim() === ""}>
            Use code
          </button>
        </form>

        {error && (
          <p className="loy__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
